/**
 * The landing's simplified front (owner, 2026-10-31: "too much text", "there
 * is nothing for the humans to see"): the live savings card in the hero
 * (components/home/SavingsMeter.tsx), the film always open under it
 * (WhyVideo.tsx), and the course overview (CourseOverview.tsx).
 *
 * The savings figures are the audited model-routing benchmark's ratio
 * (lib/savings.ts); the footnote keeps the two facts that make it honest, the
 * one task regression and that it is not a forecast, in one line.
 */
import type { Locale } from '../locale';

const en = {
  meterLabel: 'Your AI bill',
  spend: 'What you spend on AI agents a month',
  today: 'Today',
  after: 'After the course',
  keep: 'You keep',
  perMonth: 'a month',
  footnote:
    'An illustration at the cost ratio of our audited 50-task model-routing benchmark: 49/50 successes in each condition, with one task regression. Not a forecast of your bill.',
  how: 'How we measured',

  filmLabel: 'Why steer agents',
  filmTitle: 'Why you steer agents, not swim',
  filmLede: 'A short film on why the job is now steering an agent, and what that takes.',

  courseLabel: 'The course',
  courseTitle: 'Real engineering work, with your agent doing the typing.',
  courseLede:
    'Every task is a real repository with a ticket. You brief your agent, it does the work, and checks grade what it left behind.',
  howTitle: 'How it works',
  steps: [
    ['Connect your agent', 'Give it the one line above. It signs in once, with your approval.'],
    ['Brief it on your desk', 'Choose a task, read the ticket, and tell your agent what you need in your own words.'],
    ['Check, and see the cost', 'Press Check when it says it is done. Every run shows what it cost, so you learn to get the same result for less.'],
  ] as [string, string][],
  groupsTitle: 'What you work through',
  tracksTitle: 'Then go deeper',
  tasks: (n: number) => `${n} ${n === 1 ? 'task' : 'tasks'}`,
  checkpoints: (n: number) => `Plus ${n} checkpoints: assessments graded on our servers.`,
  allLevels: (n: number) => `See all ${n} levels`,
  /** One card per group of worlds (lib/course-overview.ts says which world is in which). */
  groups: {
    start: { title: 'Your first real tasks', body: 'A working terminal, a failing test, a stack trace and a bug nobody owns up to.' },
    git: { title: 'Git and teamwork', body: 'Commits, branches and remotes, recovering lost work, team history and code review.' },
    shipping: { title: 'Shipping safely', body: 'Fixes that stay fixed, releases, the pipeline, production, observability and configuration.' },
    systems: { title: 'How systems work', body: 'Programs, the network, databases and APIs, data, concurrency, security, scale and cost.' },
    owning: { title: 'Owning the codebase', body: 'Unfamiliar code, design, ownership, the platform, legacy code, and the whole job end to end.' },
    ai: { title: 'AI engineering', body: "Review an agent's pull request, call a model, give it tools, and answer from your own documents." },
    security: { title: 'Security', body: 'Untrusted input, identity, blast radius, and working securely with other people.' },
    blobor: { title: "From Blobor's history", body: 'Redo real changes from our own game, built entirely by agents, and compare with how they were made.' },
  },
};

export type LandingFrontCopy = typeof en;

export const landingFrontCopy: Record<Locale, LandingFrontCopy> = {
  en,
  // English only for now (owner, 2026-10-29).
  ar: en,
  // English only for now (owner, 2026-10-29).
  de: en,
  // English only for now (owner, 2026-10-29).
  ja: en,
};
